import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import i18n from 'i18next';
import { api, setUserInfo, clearUserInfo, getUserInfo, getTenantSlug, getAuthToken } from '../api/client';
import { checkPermission, hasWildcard } from '../utils/permissionUtils';
import { unregister as unregisterPush, refreshIfGranted as refreshPushIfGranted } from '../services/webPush';

// =============================================================================
// TYPES
// =============================================================================

interface Organization {
  _id: string;
  name: string;
  slug: string;
  subscription?: {
    isDemo?: boolean;
    expiresAt?: string;
  };
}

interface Role {
  _id: string;
  name: string;
  permissions: Record<string, number>;
  isSystem?: boolean;
  type?: 'admin' | 'user';
  limitDataAccess?: boolean;
}

interface Membership {
  _id?: string;
  organization: Organization;
  role: Role;
  joinedAt: string;
  isActive: boolean;
}

interface AuthUser {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  memberships: Membership[];
  isSuperAdmin?: boolean;
  preferences?: Record<string, unknown>;
}

interface TenantInfo {
  slug: string | null;
  name: string | null;
  isLoading: boolean;
}

interface AuthContextType {
  // User state
  user: AuthUser | null;
  currentMembership: Membership | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresOrgSelection: boolean;

  // Tenant state
  tenant: TenantInfo;

  // Auth actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: { name: string; email: string; password: string; phoneNumber?: string }) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
  selectOrganization: (orgId: string) => Promise<void>;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;

  // Role checks
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  isSuperAdmin: boolean;
}

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [currentMembership, setCurrentMembership] = useState<Membership | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresOrgSelection, setRequiresOrgSelection] = useState(false);
  const [tenant, setTenant] = useState<TenantInfo>({
    slug: getTenantSlug(),
    name: null,
    isLoading: true,
  });

  // Sync i18n language from user preferences (if set)
  const syncLanguageFromPrefs = useCallback((prefs?: Record<string, unknown>) => {
    const lang = prefs?.language;
    if (typeof lang === 'string' && lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, []);

  // Initialize user from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const stored = getUserInfo();
      if (stored) {
        setUser({
          _id: stored._id,
          name: stored.name,
          email: stored.email,
          phoneNumber: stored.phoneNumber,
          memberships: stored.memberships || [],
          isSuperAdmin: stored.isSuperAdmin,
          preferences: stored.preferences,
        });
        setCurrentMembership(stored.currentMembership || null);
        setRequiresOrgSelection(stored.requiresOrgSelection || false);
        syncLanguageFromPrefs(stored.preferences);
      }
      setIsLoading(false);
    };

    initAuth();
  }, [syncLanguageFromPrefs]);

  // Fetch tenant info from subdomain
  // Refresh the FCM token once a session is established. FCM rotates tokens,
  // and a stale one silently stops delivering. Never prompts — no-op unless
  // permission was already granted on this device.
  useEffect(() => {
    if (!user) return;
    refreshPushIfGranted().catch(() => { /* best effort */ });
  }, [user]);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      const slug = getTenantSlug();
      setTenant(prev => ({ ...prev, slug, isLoading: true }));

      if (!slug) {
        setTenant({ slug: null, name: null, isLoading: false });
        return;
      }

      try {
        const response = await api.auth.getPublicOrgInfo();
        setTenant({
          slug,
          name: response.data.name,
          isLoading: false,
        });
      } catch (error) {
        // Organization not found or other error
        setTenant({ slug, name: null, isLoading: false });
      }
    };

    fetchTenantInfo();
  }, []);

  // Login
  const login = useCallback(async (email: string, password: string) => {
    const response = await api.auth.login(email, password);
    const userData = response.data as any;

    setUserInfo(userData);
    setUser({
      _id: userData._id,
      name: userData.name,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      memberships: userData.memberships || [],
      isSuperAdmin: userData.isSuperAdmin,
      preferences: userData.preferences,
    });
    setCurrentMembership(userData.currentMembership || null);
    setRequiresOrgSelection(userData.requiresOrgSelection || false);
    syncLanguageFromPrefs(userData.preferences);
  }, [syncLanguageFromPrefs]);

  // Register
  const register = useCallback(async (data: { name: string; email: string; password: string; phoneNumber?: string }) => {
    const response = await api.auth.register(data);
    const userData = response.data as any;

    setUserInfo(userData);
    setUser({
      _id: userData._id,
      name: userData.name,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      memberships: userData.memberships || [],
      isSuperAdmin: userData.isSuperAdmin,
      preferences: userData.preferences,
    });
    setCurrentMembership(userData.currentMembership || null);
    setRequiresOrgSelection(false);
  }, []);

  // Logout
  const logout = useCallback(() => {
    // Deregister this browser's push token so a shared computer stops receiving
    // the previous user's notifications. The token is captured synchronously
    // here and passed explicitly: clearUserInfo() below runs before the axios
    // interceptor would read it, so the request would otherwise be sent
    // unauthenticated and rejected.
    unregisterPush(getAuthToken()).catch(() => { /* best effort */ });

    clearUserInfo();
    setUser(null);
    setCurrentMembership(null);
    setRequiresOrgSelection(false);
    // Optionally call backend logout to clear cookies
    api.auth.logout().catch(() => { /* ignore */ });
  }, []);

  // Switch to a different organization
  const switchOrganization = useCallback(async (orgId: string) => {
    const response = await api.auth.switchOrg(orgId);
    const userData = response.data as any;

    setUserInfo(userData);
    setUser({
      _id: userData._id,
      name: userData.name,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      memberships: userData.memberships || [],
      isSuperAdmin: userData.isSuperAdmin,
      preferences: userData.preferences,
    });
    setCurrentMembership(userData.currentMembership || null);
    setRequiresOrgSelection(false);
  }, []);

  // Select organization after login (for users with multiple memberships)
  const selectOrganization = useCallback(async (orgId: string) => {
    await switchOrganization(orgId);
  }, [switchOrganization]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.profile.get();
      const userData = response.data as any;

      // Update localStorage (merge so fields not returned by /profile are preserved)
      const stored = getUserInfo();
      if (stored) {
        setUserInfo({
          ...stored,
          ...userData,
        });
      }

      // /profile doesn't return memberships or currentMembership,
      // so fall back to stored values to avoid clearing them
      setUser({
        _id: userData._id,
        name: userData.name,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        memberships: userData.memberships || stored?.memberships || [],
        isSuperAdmin: userData.isSuperAdmin,
        preferences: userData.preferences,
      });

      // Update current membership if available from API or stored data
      if (userData.currentMembership) {
        setCurrentMembership(userData.currentMembership);
      } else if (stored?.currentMembership) {
        setCurrentMembership(stored.currentMembership);
      }

      syncLanguageFromPrefs(userData.preferences);
    } catch (error) {
      // Token invalid, logout
      logout();
    }
  }, [logout, syncLanguageFromPrefs]);

  // Permission helpers - use currentMembership.role or first membership's role
  const getCurrentRole = useCallback((): Role | undefined => {
    if (currentMembership?.role) return currentMembership.role;
    // Fallback to first active membership if no current membership set
    const firstMembership = user?.memberships?.find(m => m.isActive);
    return firstMembership?.role;
  }, [currentMembership, user]);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;

    const role = getCurrentRole();
    if (!role?.permissions) return false;

    return checkPermission(role.permissions, permission);
  }, [user, getCurrentRole]);

  const hasAnyPermission = useCallback((...permissions: string[]): boolean => {
    return permissions.some(p => hasPermission(p));
  }, [hasPermission]);

  const hasAllPermissions = useCallback((...permissions: string[]): boolean => {
    return permissions.every(p => hasPermission(p));
  }, [hasPermission]);

  // Role checks - use currentMembership or fallback
  const currentRole = getCurrentRole();
  const isOwner = hasWildcard(currentRole?.permissions) || false;
  const isAdmin = currentRole?.type === 'admin' || false;
  const isMember = !!user && !isOwner && !isAdmin;
  const isSuperAdmin = user?.isSuperAdmin || false;

  const value: AuthContextType = {
    user,
    currentMembership,
    isAuthenticated: !!user,
    isLoading,
    requiresOrgSelection,
    tenant,
    login,
    register,
    logout,
    refreshUser,
    switchOrganization,
    selectOrganization,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOwner,
    isAdmin,
    isMember,
    isSuperAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
